import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { LineChart, Line, ResponsiveContainer } from "recharts";
import {
  Zap, AlertTriangle, TrendingUp, TrendingDown, Timer, Activity, Target, Rewind, ArrowUpRight
} from "lucide-react";

const metricMeta = {
  accuracy: { label: "Accuracy", icon: Target, color: "#059669", unit: "%", higher_better: true },
  speed: { label: "Speed", icon: Timer, color: "#ea580c", unit: "", higher_better: true },
  decision_quality: { label: "Decision", icon: Zap, color: "#0d9488", unit: "", higher_better: true },
  pressure_index: { label: "Pressure", icon: AlertTriangle, color: "#e11d48", unit: "", higher_better: false },
};

/** Strava-style activity feed of last N graded attempts with pacing metrics. */
export default function PacingFeed({ trend }) {
  const navigate = useNavigate();
  const [pacing, setPacing] = useState(null);

  useEffect(() => {
    api.get("/analytics/pacing").then((r) => setPacing(r.data)).catch((err) => console.error("pacing load failed:", err));
  }, []);

  if (!trend || trend.length === 0) return null;
  const recent = [...trend].reverse().slice(0, 5); // most recent first
  const latest = recent[0];

  // Compute deltas vs previous attempt
  const previous = recent[1];
  const delta = (key) => {
    if (!previous) return null;
    return (latest[key] || 0) - (previous[key] || 0);
  };
  const spark = (key) => recent.slice().reverse().map((t, i) => ({ i, v: t[key] || 0 }));

  return (
    <Card className="p-6 rounded-md border-stone-200 shadow-none">
      <div className="flex items-center justify-between mb-1">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-stone-500">Latest workout</div>
          <h2 className="font-heading text-2xl font-semibold mt-1">Pacing analytics</h2>
        </div>
        <Badge variant="secondary" className="font-mono text-[10px] uppercase tracking-[0.2em]">
          {trend.length} activit{trend.length === 1 ? "y" : "ies"}
        </Badge>
      </div>
      <p className="text-sm text-stone-500 mb-6">
        The vital signs of your last attempt. Every graded exam updates these.
      </p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {Object.keys(metricMeta).map((key) => {
          const meta = metricMeta[key];
          const Icon = meta.icon;
          const value = latest[key] || 0;
          const d = delta(key);
          const isGood = d === null ? null : meta.higher_better ? d > 0 : d < 0;
          const DeltaIcon = d === null ? null : Math.abs(d) < 0.1 ? null : isGood ? TrendingUp : TrendingDown;
          return (
            <Card
              key={key}
              className="p-4 rounded-md border-stone-200 shadow-none hover:-translate-y-0.5 hover:shadow-md"
              style={{ transition: "transform 0.2s ease, box-shadow 0.2s ease" }}
              data-testid={`pacing-${key}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">{meta.label}</span>
                <Icon className="h-4 w-4" style={{ color: meta.color }} />
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-heading text-3xl font-bold tabular-nums" style={{ color: meta.color }}>
                  {Math.round(value)}
                </span>
                <span className="font-mono text-xs text-stone-400">{meta.unit}</span>
              </div>
              {DeltaIcon && (
                <div className={`mt-1 flex items-center gap-1 text-xs font-mono tabular-nums ${isGood ? "text-emerald-600" : "text-rose-600"}`}>
                  <DeltaIcon className="h-3 w-3" />
                  {d > 0 ? "+" : ""}{d.toFixed(1)} vs last
                </div>
              )}
              <div className="h-8 mt-3 min-w-[40px]">
                <ResponsiveContainer width="100%" height="100%" minWidth={40} minHeight={24}>
                  <LineChart data={spark(key)}>
                    <Line
                      type="monotone"
                      dataKey="v"
                      stroke={meta.color}
                      strokeWidth={2}
                      dot={false}
                      isAnimationActive={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          );
        })}
      </div>

      {/* Section time recommendations */}
      {pacing && pacing.recommendations && pacing.recommendations.length > 0 && (
        <div className="pt-6 border-t border-stone-200">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-600">Coach</div>
              <h3 className="font-heading text-lg font-semibold mt-1">Section pacing plan</h3>
              <p className="text-xs text-stone-500 mt-1">
                Based on your top {pacing.attempts_used} attempt{pacing.attempts_used > 1 ? "s" : ""} by accuracy —
                aim for these times per section next time.
              </p>
            </div>
          </div>
          <div className="space-y-2" data-testid="pacing-recommendations">
            {pacing.recommendations.map((r) => (
              <div key={r.section} className="flex items-center gap-3 p-3 border border-stone-200 rounded-md">
                <div className="h-8 w-8 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <Timer className="h-4 w-4 text-orange-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">{r.section}</div>
                  <div className="font-mono text-[10px] text-stone-500">
                    {r.avg_questions} questions · ~{r.recommended_seconds_per_q}s per Q
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-heading text-xl font-bold tabular-nums text-orange-600">
                    {r.recommended_minutes} <span className="text-sm text-stone-400">min</span>
                  </div>
                  <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-stone-500">
                    recommended
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent attempts feed */}
      <div className="pt-6 border-t border-stone-200 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg font-semibold">Recent activity</h3>
          <button
            onClick={() => navigate("/history")}
            className="text-sm text-orange-600 hover:text-orange-700 inline-flex items-center gap-1"
          >
            All history <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
        <div className="space-y-2">
          {recent.map((a) => {
            const pct = a.max_score ? Math.round((a.score / a.max_score) * 100) : 0;
            return (
              <div
                key={a.attempt_id}
                onClick={() => navigate(`/results/${a.attempt_id}`)}
                className="flex items-center gap-4 p-3 border border-stone-200 rounded-md hover:border-orange-400 cursor-pointer"
                style={{ transition: "border-color 0.15s ease" }}
                data-testid={`pacing-recent-${a.attempt_id}`}
              >
                <div className="h-10 w-10 rounded-md bg-stone-900 flex items-center justify-center flex-shrink-0">
                  <Activity className="h-5 w-5 text-orange-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm font-medium">{a.exam_title}</div>
                  <div className="font-mono text-[10px] uppercase tracking-[0.15em] text-stone-500 mt-1">
                    {new Date(a.date).toLocaleDateString()} · {Math.round((a.time_wasted_seconds || 0) / 60)}m wasted
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <PacingMini v={pct} label="score" c="#ea580c" />
                  <PacingMini v={Math.round(a.decision_quality || 0)} label="decision" c="#0d9488" />
                  <PacingMini v={Math.round(a.pressure_index || 0)} label="pressure" c="#e11d48" />
                </div>
                <Rewind className="h-4 w-4 text-stone-400 hidden md:block" />
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

function PacingMini({ v, label, c }) {
  return (
    <div>
      <div className="font-heading text-lg font-bold tabular-nums" style={{ color: c }}>{v}</div>
      <div className="font-mono text-[9px] uppercase tracking-[0.15em] text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}
